import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { updateAgent, type Agent } from './api';

// Chains supported by 0x v2 — keep in sync with backend/zerox/client.go.
const EVM_CHAINS = [
  'ethereum',
  'polygon',
  'arbitrum',
  'optimism',
  'base',
  'bsc',
  'avalanche',
];

type TradingConfig = {
  allowed_sell_assets?: string[];
  allowed_buy_assets?: string[];
  max_per_swap_usd?: string;
  max_per_day_usd?: string;
  max_slippage_bps_cap?: number;
  default_chain?: string;
};

export function TradingConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = (agent.config as TradingConfig) ?? {};

  const [sellAssets, setSellAssets] = useState('');
  const [buyAssets, setBuyAssets] = useState('');
  const [maxPerSwap, setMaxPerSwap] = useState('');
  const [maxPerDay, setMaxPerDay] = useState('');
  const [maxSlippageBps, setMaxSlippageBps] = useState('');
  const [defaultChain, setDefaultChain] = useState('polygon');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSellAssets((cfg.allowed_sell_assets ?? []).join(', '));
    setBuyAssets((cfg.allowed_buy_assets ?? []).join(', '));
    setMaxPerSwap(cfg.max_per_swap_usd ?? '');
    setMaxPerDay(cfg.max_per_day_usd ?? '');
    setMaxSlippageBps(cfg.max_slippage_bps_cap ? String(cfg.max_slippage_bps_cap) : '');
    setDefaultChain(cfg.default_chain ?? 'polygon');
    setSaved(false);
    setError(null);
  }, [agent.id]);

  const save = useMutation({
    mutationFn: () => {
      const next: TradingConfig = {
        allowed_sell_assets: parseSymbolList(sellAssets),
        allowed_buy_assets: parseSymbolList(buyAssets),
        max_per_swap_usd: maxPerSwap.trim() || undefined,
        max_per_day_usd: maxPerDay.trim() || undefined,
        max_slippage_bps_cap: maxSlippageBps.trim() ? Number(maxSlippageBps) : undefined,
        default_chain: defaultChain,
      };
      return updateAgent(orgId, agent.id, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
      setTimeout(() => setSaved(false), 3_000);
    },
    onError: (e) => {
      setError((e as Error).message || 'Could not save');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="Trading policy"
        description="Whitelists and caps the agent must respect. The runner refuses any swap that violates these — the LLM can't override them."
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Section
            title="Allowed sell assets"
            hint="Comma-separated symbols the agent may pay from. e.g. usdc, usdt, dai. Empty = block everything."
          >
            <Input
              value={sellAssets}
              onChange={(e) => setSellAssets(e.target.value)}
              placeholder="usdc, usdt, dai"
            />
          </Section>

          <Section
            title="Allowed buy assets"
            hint="Symbols the agent may receive. e.g. eth, wbtc, matic."
          >
            <Input
              value={buyAssets}
              onChange={(e) => setBuyAssets(e.target.value)}
              placeholder="eth, wbtc, matic"
            />
          </Section>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section
              title="Max per swap (USD)"
              hint="Hard cap on any single trade. 0 / empty = no cap."
            >
              <Input
                type="number"
                step="0.01"
                value={maxPerSwap}
                onChange={(e) => setMaxPerSwap(e.target.value)}
                placeholder="100"
              />
            </Section>
            <Section
              title="Max per day (USD)"
              hint="Rolling 24-hour USD throughput cap across all swaps."
            >
              <Input
                type="number"
                step="0.01"
                value={maxPerDay}
                onChange={(e) => setMaxPerDay(e.target.value)}
                placeholder="500"
              />
            </Section>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section
              title="Max slippage (bps)"
              hint="Upper bound on the slippage the LLM may request. 100 = 1%."
            >
              <Input
                type="number"
                step="1"
                value={maxSlippageBps}
                onChange={(e) => setMaxSlippageBps(e.target.value)}
                placeholder="100"
              />
            </Section>
            <Section
              title="Default chain"
              hint="Used when the LLM doesn't specify one. Must be EVM."
            >
              <select
                value={defaultChain}
                onChange={(e) => setDefaultChain(e.target.value)}
                className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
              >
                {EVM_CHAINS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Section>
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex items-center justify-end gap-3">
            {saved && <p className="font-mono text-xs text-ok">Saved</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-primary">{title}</label>
      {hint && <p className="mt-0.5 text-xs text-ink-tertiary">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function parseSymbolList(s: string): string[] {
  return s
    .split(/[,\s]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
}
